import { Button, Box, Container, Paper, Stack, Table, TableBody, TableCell, Grid, CircularProgress, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getCandidateApplications } from 'api/admin';
import { SplashScreen } from 'components/loading-screen';
import { DataGrid } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { reorderWorkHistory } from 'utils/Utils';
import exportFromJSON from 'export-from-json';

/**
 * CandidateApplications is a component that displays a candidate's applications
 * and corresponding feedback to the admin.
 *
 * @returns {JSX.Element} A React component that displays the candidate's
 * information and applications.
 */
const CandidateApplications = () => {
    const navigate = useNavigate();
    const [candidateApplications, setCandidateApplications] = useState([]);
    const [candidateDetail, setCandidateDetail] = useState({})
    const [feedbacks, setFeedbacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalExperience, setTotalExperience] = useState(0);
    const { id } = useParams();

    useEffect(() => {
        /**
         * Fetches the candidate applications and corresponding feedback for the given candidate ID
         * and updates the local state.
         * 
         * @returns {undefined}
         */
        const fetchCandidateApplications = async () => {
            try {
                const response = await getCandidateApplications({ id });
                if (response?.success && response.data != null) {
                    const { populatedApplications, userDetails, feedbacks, totalExperience } = response.data;
                    if (populatedApplications != null && userDetails != null) {
                        // console.log(populatedApplications);
                        setCandidateApplications(populatedApplications);
                        setCandidateDetail(userDetails);
                        setFeedbacks(feedbacks);
                        setTotalExperience(totalExperience);
                    }
                }
            } catch (error) {
                console.error('Error fetching jobs:', error);
            }
            setLoading(false);
        };
        fetchCandidateApplications();
    }, []);

    if (loading) {
        return (
            <Grid container justifyContent="center" alignItems="center" style={{ height: '100vh' }}>
                <CircularProgress />
            </Grid>
        );
    }

    const presentWorkHistory = candidateDetail?.candidate?.work_history?.filter(history => history.present) || [];
    const highestDesignations = presentWorkHistory?.map(designation => designation?.highest_designation).join(', ');

    /**
     * Handles navigation to the specified path.
     * 
     * @param {string} path Path to navigate to.
     * 
     * @returns {undefined}
     */
    const handleNavigation = (path) => {
        navigate(path);
    };

    /**
     * Handles the click event on a row in the DataGrid component.
     * 
     * @param {string} candidateId Unique identifier of the candidate whose row was clicked.
     * 
     * @returns {undefined}
     */
    const handleRowClick = (candidateId) => {
        handleNavigation(``);
    };

    const workHistory = reorderWorkHistory(candidateDetail?.candidate?.work_history).filter(work => work?.organization_name !== 'Work Gap') || [];

    const currentDesignation = workHistory[0]?.highest_designation || 'N/A';
    const currentWork = workHistory[0];

    const currentQualification = [
        ...new Set(
            candidateDetail?.candidate?.educations?.map((education) => education?.degree_received).filter(Boolean)
        )
    ].join(', ');

    const downloadSelectedCandidatesData = () => {
        const selectedCandidatesData = [candidateDetail];
        console.log(candidateDetail);

        const CandidatesList = selectedCandidatesData.map(candidate => {
            return {
                'Candidate Id': candidate?.candidate?._id || 'N/A',
                'Date of profile creation': dayjs(candidate?.createdAt).format('DD MMM, YYYY') || 'N/A',
                'Name': candidate?.full_name || 'N/A',
                'Gender': candidate?.sex || 'N/A',
                'Disability': candidate?.disability || 'N/A',
                'DOB': candidate?.date_of_birth ? dayjs(candidate.date_of_birth).format('DD MMM, YYYY') : 'N/A',
                'Current Location': candidate?.address?.city || 'N/A',
                'Phone number': candidate?.phone_number || 'N/A',
                'Email id': candidate?.email || 'N/A',
                'LinkedIn URL': candidate?.linkedin_profile || 'N/A',
                'Last education (Institution Name)': candidate?.candidate?.educations?.[0]?.education_institute_name || 'N/A',
                'Last education (degree name)': candidate?.candidate?.educations?.[0]?.degree_received || 'N/A',
                'Last education start and end dates':
                    candidate?.candidate?.educations?.[0]?.education_from &&
                        candidate?.candidate?.educations?.[0]?.education_to
                        ? `${dayjs(candidate.candidate.educations[0].education_from).format('MMM YYYY')} - ${dayjs(candidate.candidate.educations[0].education_to).format('MMM YYYY')}`
                        : 'N/A',
                'Work experience (Current Company Name)': currentWork?.organization_name || 'N/A',
                'Current Designation': currentWork?.highest_designation || 'N/A',
                'Current Job Function': currentWork?.last_job_function || 'N/A',
                'Job Type': (() => {
                    const jobType = currentWork?.job_type;
                    if (!jobType) return 'N/A';
                    const value = jobType.trim().toLowerCase();
                    if (value === 'both') {
                        return 'Full Time, Part Time, Temporary, Internship';
                    } else if (value === 'Full time') {
                        return 'Full Time';
                    } else if (value === 'Part time') {
                        return 'Part Time, Temporary, Internship';
                    }
                    return jobType || 'N/A';
                })(),
                'Job Location Preferences': currentWork?.work_history_location || 'N/A',
                'People managed': currentWork?.team_size_managed || 'N/A',
                'Last work experience start and end dates': currentWork?.work_history_from && currentWork?.work_history_to
                    ? `${dayjs(currentWork?.work_history_from).format('MMM YYYY')} - ${dayjs(currentWork.work_history_to).format('MMM YYYY')}`
                    : 'N/A',
                'Total work experience (Years)': totalExperience || 'N/A',
                'Expertise areas': [
                    candidate?.candidate?.educations?.[0]?.all_skills,
                    currentWork?.all_skills,
                    candidate?.candidate?.certificate?.[0]?.all_skills
                ]
                    .filter(Boolean)
                    .flat()
                    .join(', ') || 'N/A',
                'Professional Summary': candidate?.candidate?.summary || 'N/A',
                'Resume Link': candidate?._id
                    ? `=HYPERLINK("${process.env.REACT_APP_END_POINT}/api/services/download-candidate-resume?user_id=${candidate._id}", "Download Resume")`
                    : 'N/A',
                'Candidate feedback': candidate?.feedback?.map(feedback => feedback?.feedback) || 'N/A',
                // 'Salary': candidate?.salary || 'N/A',
                'Job preference': (() => {
                    const jobType = candidate?.candidate?.job_preferences?.job_type?.[0];
                    if (!jobType) return 'N/A';
                    const value = jobType.trim().toLowerCase();
                    if (value === 'both') {
                        return 'Full Time, Part Time, Temporary, Internship';
                    } else if (value === 'full time') {
                        return 'Full Time';
                    } else if (value === 'part time') {
                        return 'Part Time, Temporary, Internship';
                    }
                    return jobType || 'N/A';
                })(),
                'Preferred work format': candidate?.candidate?.job_preferences?.work_format?.[0] || 'N/A',
                'Preferred working hours': candidate?.candidate?.job_preferences?.preferred_job_shifts || 'N/A',
                'Preferred locations': candidate?.candidate?.job_preferences?.prefered_location || 'N/A',
                'Current compensation - Fixed': candidate?.candidate?.job_preferences?.fixed_ctc || 'N/A',
                'Current compensation - variable': candidate?.candidate?.job_preferences?.variable_ctc || 'N/A',
                'Expected annual compensation': candidate?.candidate?.job_preferences?.expected_ctc || 'N/A',
                'Notice period': candidate?.candidate?.job_preferences?.notice_period || 'N/A',
                'Part - time Hourly rate': candidate?.candidate?.job_preferences?.hourly_rate || 'N/A',
                'Job Function': currentWork?.last_job_function || 'N/A',
                'Part - time availability(hours / week)': candidate?.candidate?.job_preferences?.hour || 'N/A',
            };
        });

        const fileName = `${candidateDetail?.full_name}`;
        const exportType = exportFromJSON.types.csv;
        exportFromJSON({ data: CandidatesList, fileName, exportType });
    };

    return (
        <Container>
            <Box style={{ marginTop: '80px' }}>
                <Paper elevation={3} sx={{ padding: '1rem' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" component="div">
                            Candidate Information
                        </Typography>
                        {/* 🔹 Download Resume Link */}
                        {candidateDetail?._id ? (
                            <Box display="flex" gap={2}>
                                <a
                                    href={`${process.env.REACT_APP_END_POINT}/api/services/download-candidate-resume?user_id=${candidateDetail._id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ fontSize: '0.9rem', color: '#1976d2', textDecoration: 'none' }}
                                >
                                    Download Resume
                                </a>
                                <button
                                    onClick={downloadSelectedCandidatesData}
                                    style={{
                                        fontSize: '0.9rem',
                                        color: '#1976d2',
                                        textDecoration: 'none',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: 0
                                    }}
                                >
                                    Download Profile
                                </button>
                            </Box>
                        ) : (
                            <Typography variant="body2" color="textSecondary">
                                N/A
                            </Typography>
                        )}


                    </Box>
                    <Box sx={{ marginTop: '2rem' }}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <Box>
                                <Typography variant="p" component="div" sx={{ marginBottom: '0.5rem', fontSize: '0.8rem' }}>
                                    Name: {candidateDetail?.full_name || 'N/A'}
                                </Typography>
                                <Typography variant="p" component="div" sx={{ marginBottom: '0.5rem', fontSize: '0.8rem' }}>
                                    Email: {candidateDetail?.email || 'N/A'}
                                </Typography>
                                <Typography variant="p" component="div" sx={{ marginBottom: '0.5rem', fontSize: '0.8rem' }}>
                                    Phone: {candidateDetail?.phone_number || 'N/A'}
                                </Typography>
                                <Typography variant="p" component="div" sx={{ marginBottom: '0.5rem', fontSize: '0.8rem' }}>
                                    Current Designation: {currentDesignation || 'N/A'}
                                </Typography>
                                <Typography variant="p" component="div" sx={{ marginBottom: '0.5rem', fontSize: '0.8rem' }}>
                                    Qualification: {currentQualification || 'N/A'}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                </Paper>
            </Box>

            <Box sx={{ marginTop: '2rem' }}>
                <Paper elevation={3} sx={{ padding: '1rem' }}>
                    <Typography variant="h6" component="div">
                        Candidate Applications
                    </Typography>
                    <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>#</TableCell>
                                    <TableCell>Company Name</TableCell>
                                    <TableCell>Job Title</TableCell>
                                    <TableCell>Application Date</TableCell>
                                    <TableCell>Application Status</TableCell>
                                    <TableCell>Feedback Input</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {candidateApplications?.map((application, index) => (
                                    <TableRow key={application._id}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>{application?.job_id?.company ? application?.job_id?.company : 'N/A'}</TableCell>
                                        <TableCell>{application?.job_id?.title}</TableCell>
                                        <TableCell>{application?.applied_at ? dayjs(application?.applied_at).format('DD-MM-YYYY ') : 'N/A'}</TableCell>
                                        <TableCell>{application?.status === 'saved' ? 'not applied' : application?.status}</TableCell>
                                        <TableCell>
                                            {feedbacks ? feedbacks
                                                .filter((feedback) => feedback?.application === application?._id)
                                                .map((feedback) => (
                                                    <Typography key={feedbacks?._id} variant="body2" component="div">
                                                        {feedback?.feedback}
                                                    </Typography>
                                                )) : 'N/A'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>
        </Container>
    );
};

export default CandidateApplications;
